import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Workflow } from '@couch/shared';
import { api, errorMessage } from '../../api/client';

/** Which picture each LoadImage node uses for the next run: its own, or the user's choice. */
export type InputChoices = Record<string, string>;

interface Props { machineId: string; workflow: Workflow; choices: InputChoices; onChange: (choices: InputChoices) => void }

const viewUrl = (machineId: string, name: string) => `/api/v1/machines/${encodeURIComponent(machineId)}/inputs/view?name=${encodeURIComponent(name)}`;

function NodeInput({ machineId, nodeId, current, names, onPick }: { machineId: string; nodeId: string; current: string; names: string[]; onPick: (name: string) => void }) {
  const queryClient = useQueryClient();
  const upload = useMutation({
    mutationFn: (file: File) => api.uploadInput(machineId, file),
    onSuccess: async ({ image }) => { onPick(image.name); await queryClient.invalidateQueries({ queryKey: ['inputs', machineId] }); },
  });
  return (
    <div className="input-image">
      {current && <img src={viewUrl(machineId, current)} alt={`Input for node ${nodeId}: ${current}`} />}
      <div className="input-image-controls">
        <label className="button-link">
          {upload.isPending ? 'Uploading…' : 'Upload a photo'}
          <input type="file" accept="image/png,image/jpeg,image/webp" className="visually-hidden" disabled={upload.isPending}
            onChange={(e) => { const file = e.target.files?.[0]; if (file) upload.mutate(file); e.target.value = ''; }} />
        </label>
        <label>or use{' '}
          <select value={current} onChange={(e) => { onPick(e.target.value); }} aria-label={`Picture for node ${nodeId}`}>
            {!names.includes(current) && current && <option value={current}>{current}</option>}
            {names.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
      </div>
      {upload.isError && <p role="alert" className="form-error">{errorMessage(upload.error)}</p>}
    </div>
  );
}

/** Input pictures for recipes that start from a photo (doc 33). */
export function InputImagesPanel({ machineId, workflow, choices, onChange }: Props) {
  const loaders = Object.entries(workflow).filter(([, node]) => node.class_type === 'LoadImage');
  const inputs = useQuery({ queryKey: ['inputs', machineId], queryFn: () => api.listInputs(machineId), enabled: loaders.length > 0 && machineId !== '' });
  if (loaders.length === 0 || machineId === '') return null;
  const names = inputs.data?.images.map((i) => i.name) ?? [];
  return (
    <section className="panel" aria-labelledby="inputs-title">
      <h2 id="inputs-title">Input {loaders.length > 1 ? 'pictures' : 'picture'}</h2>
      {loaders.map(([id, node]) => {
        const own = typeof node.inputs.image === 'string' ? node.inputs.image : '';
        return (
          <NodeInput key={id} machineId={machineId} nodeId={id} names={names} current={choices[id] ?? own}
            onPick={(name) => { onChange({ ...choices, [id]: name }); }} />
        );
      })}
      {inputs.isError && <p role="alert" className="form-error">{errorMessage(inputs.error)}</p>}
    </section>
  );
}
